/**
 * The shell's layout rules, kept free of Vue and the DOM so they can be tested.
 *
 * Three widths, three behaviours:
 *
 * - **mobile** (< 768px) — the sidebar is an off-canvas drawer behind a menu
 *   button. This is the company portal's main case: it runs inside the Lark
 *   mobile client.
 * - **medium** (768–1023px) — an icon rail. Expanding it lays the full
 *   sidebar *over* the content rather than pushing it, because at these widths
 *   a 240px column leaves the page too little room to be usable.
 * - **wide** (≥ 1024px) — a full sidebar the person may collapse to the rail;
 *   that choice is remembered.
 */
export type ShellMode = 'mobile' | 'medium' | 'wide'

export const SHELL_MEDIUM_QUERY = '(min-width: 768px)'
export const SHELL_WIDE_QUERY = '(min-width: 1024px)'

export function shellMode(isMediumUp: boolean, isWide: boolean): ShellMode {
  if (isWide) return 'wide'
  return isMediumUp ? 'medium' : 'mobile'
}

export interface ShellLayoutInput {
  mode: ShellMode
  /** The remembered wide-screen preference. */
  collapsed: boolean
  /** The rail is temporarily expanded over the content (medium only). */
  peek: boolean
  /** The drawer is open (mobile only). */
  drawerOpen: boolean
}

export interface ShellLayout {
  /** Icons only: labels are visually hidden and shown as tooltips. */
  rail: boolean
  /** The sidebar sits over the content with a backdrop (drawer or peek). */
  overlay: boolean
  /** How far the content is pushed right, in the sidebar's own width units. */
  inset: 'none' | 'rail' | 'full'
}

export function shellLayout({ mode, collapsed, peek, drawerOpen }: ShellLayoutInput): ShellLayout {
  switch (mode) {
    case 'mobile':
      return { rail: false, overlay: drawerOpen, inset: 'none' }
    case 'medium':
      return { rail: !peek, overlay: peek, inset: 'rail' }
    case 'wide':
      return { rail: collapsed, overlay: false, inset: collapsed ? 'rail' : 'full' }
  }
}

/**
 * Read the remembered preference. Storage can be absent or throw (a private
 * window, blocked site data, the Lark webview with storage off), and none of
 * that is worth breaking the layout over: the sidebar simply starts expanded.
 */
export function readCollapsed(storage: Pick<Storage, 'getItem'> | undefined, key: string): boolean {
  try {
    return storage?.getItem(key) === '1'
  } catch {
    return false
  }
}

export function writeCollapsed(storage: Pick<Storage, 'setItem'> | undefined, key: string, collapsed: boolean): void {
  try {
    storage?.setItem(key, collapsed ? '1' : '0')
  } catch {
    // Not remembered this time; the toggle itself still works.
  }
}

/**
 * Whether a click on a link should be handed to the app's router. A modified
 * or non-primary click is the browser's (new tab, new window, download).
 */
export function isPlainLeftClick(event: Pick<MouseEvent, 'button' | 'metaKey' | 'ctrlKey' | 'shiftKey' | 'altKey' | 'defaultPrevented'>): boolean {
  return !event.defaultPrevented && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey
}
